import { useNostr } from '@nostrify/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useMemo } from 'react';
import { dedupeEvents } from '@/lib/jumble';
import { useSocial, isEventMuted } from '@/contexts/SocialContext';
import { useCurrentUser } from './useCurrentUser';

/** Infinite global/relay-set feed (kind 1 notes), relay-targeted via nostr.group. */
export function useRelayFeed(relayUrls: string[], opts?: { showReplies?: boolean; enabled?: boolean }) {
  const { nostr } = useNostr();
  const { mutedPubkeys, mutedWords } = useSocial();
  const showReplies = opts?.showReplies ?? true;

  const query = useInfiniteQuery({
    queryKey: ['jumble', 'relay-feed', [...relayUrls].sort(), showReplies],
    enabled: (opts?.enabled ?? true) && relayUrls.length > 0,
    queryFn: async ({ pageParam, signal }) => {
      const group = relayUrls.length > 0 ? nostr.group(relayUrls) : nostr;
      const filter: Record<string, unknown> = { kinds: [1], limit: 30 };
      if (pageParam) (filter as { until: number }).until = pageParam;
      const events = (await group.query([filter], { signal: signal ?? AbortSignal.timeout(4000) })) as NostrEvent[];
      return dedupeEvents(events);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 10) return undefined;
      return lastPage[lastPage.length - 1].created_at - 1;
    },
    initialPageParam: undefined as number | undefined,
    staleTime: 30_000,
  });

  const events = useMemo(() => {
    const all = dedupeEvents((query.data?.pages ?? []).flat());
    return all.filter((e) => {
      if (!showReplies && e.tags.some((t) => t[0] === 'e')) return false;
      if (isEventMuted(e.content, e.pubkey, mutedPubkeys, mutedWords)) return false;
      return true;
    });
  }, [query.data, showReplies, mutedPubkeys, mutedWords]);

  return { ...query, events };
}

/** Following feed: notes from people the user follows (kind 3 contact list -> kind 1). */
export function useFollowingFeed(opts?: { enabled?: boolean }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutedPubkeys, mutedWords, showReplies } = useSocial();

  const follows = useQuery({
    queryKey: ['jumble', 'follows', user?.pubkey ?? 'anon'],
    enabled: !!user,
    queryFn: async ({ signal }) => {
      const [list] = await nostr.query(
        [{ kinds: [3], authors: [user!.pubkey], limit: 1 }],
        { signal: signal ?? AbortSignal.timeout(3000) },
      );
      return (list?.tags ?? []).filter((t) => t[0] === 'p').map((t) => t[1]);
    },
    staleTime: 5 * 60_000,
  });

  const query = useInfiniteQuery({
    queryKey: ['jumble', 'following-feed', user?.pubkey ?? 'anon', follows.data?.length ?? 0, showReplies],
    enabled: (opts?.enabled ?? true) && !!user && !!follows.data && follows.data.length > 0,
    queryFn: async ({ pageParam, signal }) => {
      const authors = [...(follows.data ?? []), user!.pubkey].slice(0, 400);
      const filter: Record<string, unknown> = { kinds: [1], authors, limit: 30 };
      if (pageParam) (filter as { until: number }).until = pageParam;
      const events = (await nostr.query([filter], { signal: signal ?? AbortSignal.timeout(4000) })) as NostrEvent[];
      return dedupeEvents(events);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 10) return undefined;
      return lastPage[lastPage.length - 1].created_at - 1;
    },
    initialPageParam: undefined as number | undefined,
    staleTime: 30_000,
  });

  const events = useMemo(() => {
    const all = dedupeEvents((query.data?.pages ?? []).flat());
    return all.filter((e) => {
      if (!showReplies && e.tags.some((t) => t[0] === 'e')) return false;
      if (isEventMuted(e.content, e.pubkey, mutedPubkeys, mutedWords)) return false;
      return true;
    });
  }, [query.data, showReplies, mutedPubkeys, mutedWords]);

  return { ...query, events, follows: follows.data ?? [], followsLoading: follows.isLoading };
}

/** Single event by id (thread root / note page). */
export function useEventById(id: string | undefined, relayHints?: string[]) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['jumble', 'event', id],
    enabled: !!id,
    queryFn: async ({ signal }) => {
      const source = relayHints && relayHints.length > 0 ? nostr.group(relayHints) : nostr;
      const [event] = (await source.query([{ ids: [id!] }], { signal: signal ?? AbortSignal.timeout(4000) })) as NostrEvent[];
      if (!event) throw new Error('Event not found');
      return event;
    },
    retry: 1,
  });
}

/** Replies + reactions + reposts for one event id (single batched query, split in JS). */
export function useThreadChildren(eventId: string | undefined) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['jumble', 'children', eventId],
    enabled: !!eventId,
    queryFn: async ({ signal }) => {
      const all = (await nostr.query(
        [{ kinds: [1, 6, 7], '#e': [eventId!], limit: 200 }],
        { signal: signal ?? AbortSignal.timeout(4000) },
      )) as NostrEvent[];
      const replies = all.filter((e) => e.kind === 1);
      const reposts = all.filter((e) => e.kind === 6);
      const reactions = all.filter((e) => e.kind === 7);
      return { replies: dedupeEvents(replies), reposts, reactions };
    },
    staleTime: 15_000,
  });
}

/** Ancestors chain for a reply (walk up via e tags). */
export function useAncestors(event: NostrEvent | undefined) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['jumble', 'ancestors', event?.id],
    enabled: !!event,
    queryFn: async ({ signal }) => {
      const chain: NostrEvent[] = [];
      const seen = new Set<string>();
      let current = event!;
      for (let i = 0; i < 6; i++) {
        const parentId = current.tags.find((t) => t[0] === 'e')?.[1];
        if (!parentId || seen.has(parentId)) break;
        seen.add(parentId);
        const [parent] = (await nostr.query([{ ids: [parentId] }], { signal: signal ?? AbortSignal.timeout(3000) })) as NostrEvent[];
        if (!parent) break;
        chain.unshift(parent);
        current = parent;
      }
      return chain;
    },
    staleTime: 60_000,
  });
}

/** Profile notes (kind 1 by author). */
export function useProfileNotes(pubkey: string | undefined) {
  const { nostr } = useNostr();
  return useInfiniteQuery({
    queryKey: ['jumble', 'profile-notes', pubkey],
    enabled: !!pubkey,
    queryFn: async ({ pageParam, signal }) => {
      const filter: Record<string, unknown> = { kinds: [1], authors: [pubkey!], limit: 20 };
      if (pageParam) (filter as { until: number }).until = pageParam;
      const events = (await nostr.query([filter], { signal: signal ?? AbortSignal.timeout(4000) })) as NostrEvent[];
      return dedupeEvents(events);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 5) return undefined;
      return lastPage[lastPage.length - 1].created_at - 1;
    },
    initialPageParam: undefined as number | undefined,
  });
}

/** Long-form articles (kind 30023) for explore page. */
export function useArticles(relayUrls: string[]) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['jumble', 'articles', [...relayUrls].sort()],
    enabled: relayUrls.length > 0,
    queryFn: async ({ signal }) => {
      const group = nostr.group(relayUrls);
      const events = (await group.query([{ kinds: [30023], limit: 30 }], { signal: signal ?? AbortSignal.timeout(5000) })) as NostrEvent[];
      return dedupeEvents(events);
    },
    staleTime: 60_000,
  });
}
